"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { markLoadingActive, markLoadingComplete } from "@/lib/loading-screen-gate";
import {
  heavyAssetsForPath,
  downloadHeavyAssets,
  splashDisabledForPath,
} from "@/lib/asset-preloader";

/**
 * v6.13.37 — sessionStorage gate.
 *
 * The loading splash should only appear ONCE per session. Subsequent
 * client-side navigations (Link → Link) keep the layout mounted and
 * therefore the splash never re-renders, but a hard reload (back
 * button to a stale tab, manual refresh, or some browsers' restore-
 * after-restart) used to re-trigger the full 1.5-2.4 s splash on
 * every page view. We persist the "boot already done" flag in
 * sessionStorage so the same browsing session (across reloads)
 * skips the overlay entirely after the very first paint.
 */
const SESSION_GATE_KEY = "rg:loaded-once";
function alreadyLoadedThisSession(): boolean {
  if (typeof window === "undefined") return false;
  try { return sessionStorage.getItem(SESSION_GATE_KEY) === "1"; } catch { return false; }
}
function markSessionLoaded() {
  if (typeof window === "undefined") return;
  try { sessionStorage.setItem(SESSION_GATE_KEY, "1"); } catch {}
}

/**
 * Heavy-scene routes (the home Spline galaxy) must ALWAYS hold the splash
 * until the scene has actually downloaded + painted — even on a same-session
 * reload — otherwise the session gate skips the overlay, the site reveals
 * instantly, and the 23 MB galaxy "pops in" a second or two later over a
 * blank backdrop (owner: "scene takes a while to appear, it should be fully
 * loaded in the loading screen before the site loads"). Light routes keep
 * the fast once-per-session skip.
 */
function forceSplashThisRoute(): boolean {
  if (typeof window === "undefined") return false;
  try { return heavyAssetsForPath(window.location.pathname).length > 0; } catch { return false; }
}

/** Top-level routes the splash should warm up so subsequent client
 *  navigations are instant and never trigger a Suspense fallback. */
const PREFETCH_ROUTES = [
  "/",
  "/store-list",
  "/exclusive-mentorships",
  "/evade-cancelations",
  "/shop-methods",
  "/our-service",
  "/vouches",
];

// Module-import side-effect: mark the loading screen as active
// SYNCHRONOUSLY before any other component reads the gate flag.
// The layout imports <LoadingScreen> at the top of <body>, ahead
// of every entrance component in the page tree, so this flag is
// already set when GlassCard / PathCard / MeshEntrance / etc.
// run their mount-time `useEntranceReady()` initializer.
//
// v6.13.37: skip marking active if the session has already booted —
// otherwise entrance components on a soft-refreshed page would wait
// for a `refgd:loading-complete` event that never comes (because
// LoadingScreen never renders).
if (
  typeof window !== "undefined" &&
  !splashDisabledForPath(window.location.pathname) &&
  (!alreadyLoadedThisSession() || forceSplashThisRoute())
) {
  markLoadingActive();
}

/**
 * LoadingScreen — full-screen cinematic boot overlay with a REAL
 * progress tracker.
 *
 * Why this exists
 * ───────────────
 * On a fresh page load, the browser is doing a LOT of work in the
 * first ~1.5 seconds: image decoding, font swap, GPU layer
 * promotion, framer-motion JIT, WebGL init. If the user can scroll
 * during that window, every animation pays the layer-creation tax
 * in real time and feels janky. Holding the user behind this
 * overlay until the page is *actually* warm fixes that.
 *
 * The progress tracker is a TRUE measurement, not a fake timer:
 *
 *   • IMAGES — every <img> currently in the DOM contributes 1 unit;
 *     each fires its load event when the browser has decoded it
 *   • FONTS — every FontFace registered with document.fonts is
 *     1 unit; resolves when its load promise settles
 *   • WINDOW — the 'load' event (everything in <head>, all CSS
 *     and stylesheet @font-face declarations) is 1 weighted block
 *   • PAINT — two rAF cycles after everything else, to guarantee
 *     the React tree behind the overlay has fully painted
 *   • STAGE — a soft minimum 1.5 s so the overlay never flashes
 *     by faster than the user can read it
 *
 * The bar value is the WEIGHTED REAL ratio of all the above. It
 * physically cannot reach 100 % until every signal resolves. A
 * very gentle time-based floor is added so the bar always shows
 * forward motion (never sits at 0 % for 800 ms while the first
 * batch of images is still in-flight) — but that floor caps at
 * 88 %, never at 100 %. 100 % only ever fires when the actual
 * Promise.all() of all real signals resolves.
 *
 * Once truly ready: pause briefly at 100 %, fade overlay over
 * 800 ms, then unmount. Subsequent scrolls have no overlay cost.
 */

const PHASES = [
  "Initialising cosmos",
  "Loading paths",
  "Aligning constellations",
  "Tuning the journey",
  "Ready",
];

export default function LoadingScreen() {
  const router = useRouter();
  // Read the route the SSR-consistent way. usePathname() resolves to the same
  // value on the server render and the first client render, so the splash
  // opt-out below can drop the overlay from the server HTML itself instead of
  // only in a post-mount effect.
  const pathname = usePathname();
  // v6.13.37 — if this session has already shown the splash once,
  // skip it entirely on reloads / additional mounts. Initialise
  // BOTH `removed` and `visible` from the gate so the very first
  // render returns null without flashing the overlay for a frame.
  const [progress, setProgress] = useState(100);
  const [phase, setPhase] = useState(PHASES[4]);
  /* v6.13.59 — Initial state MUST be identical between SSR and the
     very first client render, otherwise React #418 (hydration
     mismatch) detonates the whole tree.

     Previously these initialisers read `alreadyLoadedThisSession()`
     directly, which inspects sessionStorage. SSR always returned
     {visible:true, removed:false}; a returning visitor's first CSR
     returned {visible:false, removed:true} → the server emitted the
     splash overlay, the client hydrated with `null`, and React
     bailed on hydration with the symptom seen in production
     (Minified React error #418 in the console, then the page falls
     back to client-only render and any state captured during SSR
     is discarded).

     Fix: always start in the SSR-equivalent "splash visible" state.
     The mount-effect below then immediately jumps to removed=true
     for returning visitors, so they see at most one ~16 ms frame
     of the overlay instead of an aborted hydration. */
  const [visible, setVisible] = useState(true);
  const [removed, setRemoved] = useState(false);
  // Keep last reported value so the bar never goes backwards
  const lastShownRef = useRef(0);

  // v6.13.37 — Even though `removed` short-circuits the render,
  // we still want subsequent navigations to feel instant — so
  // prefetch every top-level route once on mount regardless.
  useEffect(() => {
    PREFETCH_ROUTES.forEach((r) => {
      try { router.prefetch(r); } catch { /* noop */ }
    });
    // Register the scene-cache service worker so the heavy ~23 MB Spline
    // galaxy is downloaded once and then served from the local cache on every
    // future visit (it intercepts Spline's own fetch too). Scoped to only the
    // scene file, so it never touches HTML/JS and can't serve a stale deploy.
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => { /* noop */ });
    }
  }, [router]);

  useEffect(() => {
    // /community (and subpages) explicitly opt out of the splash entirely —
    // no cinematic boot for the community hub. Open the entrance gate and
    // drop the overlay immediately so the section is instantly usable.
    if (splashDisabledForPath(window.location.pathname)) {
      markLoadingComplete();
      setVisible(false);
      setRemoved(true);
      return;
    }
    // Already done this session → don't even spin up the trackers.
    // EXCEPTION: heavy-scene routes (home galaxy) always re-run so the
    // splash holds until the Spline scene has painted, never popping in late.
    if (alreadyLoadedThisSession() && !forceSplashThisRoute()) {
      // Make sure the entrance gate is open for any component that
      // mounted after us.
      markLoadingComplete();
      // v6.13.59 — Hide the splash immediately. Initial state is now
      // {visible:true, removed:false} for SSR/hydration parity, so
      // without these calls a returning visitor would stay on the
      // overlay until they navigated.
      setVisible(false);
      setRemoved(true);
      return;
    }
    // Lock body so the user can't start scrolling on a half-warm tree
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    // Route-aware heavy asset(s): the home page pulls a ~23 MB Spline
    // galaxy. We fully download it behind this splash (warming the HTTP
    // cache so the scene component reuses it) and only lift once it's in.
    // Light routes have none and keep the fast boot.
    const heavyAssets = heavyAssetsForPath(window.location.pathname);
    const hasHeavyAsset = heavyAssets.length > 0;
    const assetAbort = new AbortController();

    const startTime = performance.now();
    const MIN_DURATION = 1500;
    let cancelled = false;
    let rafId = 0;
    let timerA = 0;
    let timerB = 0;
    let timerC = 0;

    // ── REAL trackers ──────────────────────────────────────────────
    const state = {
      imgsTotal: 0,
      imgsLoaded: 0,
      fontsTotal: 0,
      fontsLoaded: 0,
      windowLoaded: document.readyState === "complete",
      firstPaintDone: false,
      minStallDone: false,
      everythingResolved: false,
      // 0..1 real download fraction of the route's heavy remote asset(s).
      sceneDlPct: 0,
    };

    function commitProgress() {
      if (cancelled) return;
      // Components of real progress (each is a 0..1 ratio)
      const imgPct =
        state.imgsTotal === 0 ? 1 : state.imgsLoaded / state.imgsTotal;
      const fontPct =
        state.fontsTotal === 0 ? 1 : state.fontsLoaded / state.fontsTotal;
      const winPct = state.windowLoaded ? 1 : 0;
      const paintPct = state.firstPaintDone ? 1 : 0;
      const stallPct = state.minStallDone ? 1 : 0;

      // Weighted real composite. On a route with a heavy remote asset
      // (the home Spline galaxy) the actual MEGABYTE download dominates
      // the bar so the % reflects genuine progress toward "fully
      // downloaded"; the other signals are minor contributors. Light
      // routes keep the original first-paint-weighted mix.
      const real = hasHeavyAsset
        ? state.sceneDlPct * 0.6 +
          imgPct * 0.15 +
          fontPct * 0.1 +
          winPct * 0.05 +
          paintPct * 0.05 +
          stallPct * 0.05
        : imgPct * 0.45 +
          fontPct * 0.20 +
          winPct * 0.15 +
          paintPct * 0.10 +
          stallPct * 0.10;

      // Soft time floor so the bar shows motion even if the first
      // batch of resources hasn't reported yet. Caps at 88 % — only
      // the everythingResolved Promise.all() can take it to 100 %.
      // On heavy-asset routes the floor is held much lower (35 %) so a
      // fast time-curve can't outrun a still-downloading 23 MB scene and
      // falsely imply it's almost done.
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / 1200);
      const eased = 1 - Math.pow(1 - t, 3);
      const floor = hasHeavyAsset
        ? Math.min(0.35, eased * 0.35)
        : Math.min(0.88, eased * 0.78);

      const candidate = Math.max(real, floor);
      const cap = state.everythingResolved ? 1.0 : 0.95;
      const display = Math.min(cap, candidate);

      // Monotonic — never go backwards
      const pct = Math.max(lastShownRef.current, Math.round(display * 100));
      lastShownRef.current = pct;
      setProgress(pct);

      if (pct < 25) setPhase(PHASES[0]);
      else if (pct < 55) setPhase(PHASES[1]);
      else if (pct < 80) setPhase(PHASES[2]);
      else if (pct < 100) setPhase(PHASES[3]);
      else setPhase(PHASES[4]);
    }

    function tick() {
      if (cancelled) return;
      commitProgress();
      if (!state.everythingResolved) rafId = requestAnimationFrame(tick);
    }

    // Start the rAF loop immediately so the bar shows life
    rafId = requestAnimationFrame(tick);

    // ── Discover trackable resources after the first paint so the
    //    React tree behind us has had a chance to mount its <img>s.
    const discoverTimer = window.setTimeout(() => {
      if (cancelled) return;
      const imgs = Array.from(document.querySelectorAll("img"));
      state.imgsTotal = imgs.length;
      imgs.forEach((img) => {
        if (img.complete && img.naturalHeight > 0) {
          state.imgsLoaded++;
        } else {
          const onDone = () => {
            state.imgsLoaded++;
            commitProgress();
          };
          img.addEventListener("load", onDone, { once: true });
          img.addEventListener("error", onDone, { once: true });
        }
      });
      // Some lazy-loaded images may never fire while `loading="lazy"`
      // and below the fold. We treat them as loaded after a short
      // grace window so they can't block the bar forever.
      window.setTimeout(() => {
        if (cancelled) return;
        const remaining = state.imgsTotal - state.imgsLoaded;
        if (remaining > 0) {
          state.imgsLoaded = state.imgsTotal;
          commitProgress();
        }
      }, 4500);
      commitProgress();
    }, 250);

    // ── Fonts: register every FontFace, resolve on each load ──
    if (typeof document !== "undefined" && document.fonts) {
      // Snapshot count after a short delay so late `<link rel=stylesheet>`
      // @font-face rules have had time to register.
      window.setTimeout(() => {
        if (cancelled) return;
        const all = Array.from(document.fonts as Iterable<FontFace>);
        state.fontsTotal = all.length || 1;
        all.forEach((f) => {
          if (f.status === "loaded") state.fontsLoaded++;
          else
            f.load()
              .then(() => {
                state.fontsLoaded++;
                commitProgress();
              })
              .catch(() => {
                state.fontsLoaded++;
                commitProgress();
              });
        });
        commitProgress();
      }, 100);
    }

    // ── Window load (CSS, fonts via @font-face, deferred scripts) ──
    const onWindowLoad = () => {
      state.windowLoaded = true;
      commitProgress();
    };
    if (document.readyState === "complete") {
      state.windowLoaded = true;
    } else {
      window.addEventListener("load", onWindowLoad, { once: true });
    }

    // ── Two rAF cycles after window load so we know the React tree
    //    has had a real layout + paint pass.
    const paintWaiter = new Promise<void>((resolve) => {
      const trigger = () => {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            state.firstPaintDone = true;
            commitProgress();
            resolve();
          }),
        );
      };
      if (document.readyState === "complete") trigger();
      else window.addEventListener("load", trigger, { once: true });
    });

    // ── Min stall: never finish faster than the user can register
    //    that the overlay even appeared.
    const minStallPromise = new Promise<void>((r) =>
      window.setTimeout(() => {
        state.minStallDone = true;
        commitProgress();
        r();
      }, MIN_DURATION),
    );

    // ── Window load promise ──
    const windowLoadPromise = new Promise<void>((r) => {
      if (document.readyState === "complete") r();
      else window.addEventListener("load", () => r(), { once: true });
    });

    // ── Fonts ready promise ──
    const fontsReadyPromise: Promise<unknown> =
      typeof document !== "undefined" && document.fonts && document.fonts.ready
        ? document.fonts.ready
        : Promise.resolve();

    // ── Scene-ready promise: any cinematic scene component
    //    (ChipScroll on the evade page, etc.) dispatches a
    //    `refgd:scene-ready` window event once its canvas/3D scene
    //    has actually painted its first frame. We wait for it here
    //    so the loading overlay never fades out exposing a blank
    //    canvas. If no such scene is on the current page (home,
    //    mentorships, etc.), the timeout below resolves immediately
    //    after a short grace window so it doesn't extend page loads
    //    that don't need it.
    // A heavy 3D scene (the home-page Spline galaxy) dispatches
    // `refgd:scene-pending` the instant it begins mounting. When that
    // fires we give the scene a much longer window to paint its first
    // frame so the splash never lifts onto an empty backdrop. Pages with
    // no scene never see the event and keep the fast 1500ms grace.
    let scenePending =
      typeof window !== "undefined" &&
      !!(window as unknown as { __refgdScenePending?: boolean })
        .__refgdScenePending;
    const onScenePending = () => {
      scenePending = true;
    };
    window.addEventListener(
      "refgd:scene-pending",
      onScenePending as EventListener,
    );

    const sceneReadyPromise = new Promise<void>((resolve) => {
      let resolved = false;
      let graceTimer = 0;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        window.clearTimeout(graceTimer);
        window.removeEventListener(
          "refgd:scene-ready",
          finish as EventListener,
        );
        window.removeEventListener(
          "refgd:scene-pending",
          onScenePending as EventListener,
        );
        resolve();
      };
      window.addEventListener("refgd:scene-ready", finish as EventListener, {
        once: true,
      });
      // Heavy-asset routes (the home galaxy) wait for the REAL
      // scene-ready event — no auto-finish grace — so the splash never
      // lifts onto a blank canvas while the freshly-downloaded 23 MB
      // scene is still parsing into its first painted frame. The
      // ceilingPromise below is the only backstop on those routes.
      if (hasHeavyAsset) return;
      // First checkpoint at 1500ms: pages WITHOUT a scene resolve here so
      // they're never held back. If a scene announced itself pending, we
      // extend the grace to give the galaxy time to actually paint.
      graceTimer = window.setTimeout(() => {
        if (!scenePending) finish();
        else graceTimer = window.setTimeout(finish, 6000);
      }, 1500);
    });

    // ── Image promise: resolves when ALL images have either loaded
    //    or hit the 4.5 s grace fallback above ──
    const imagesPromise = new Promise<void>((resolve) => {
      const check = window.setInterval(() => {
        if (cancelled) {
          clearInterval(check);
          return;
        }
        if (
          state.imgsTotal > 0 &&
          state.imgsLoaded >= state.imgsTotal
        ) {
          clearInterval(check);
          resolve();
        }
      }, 100);
      // Hard ceiling — never block beyond 6 s on images alone
      window.setTimeout(() => {
        clearInterval(check);
        resolve();
      }, 6000);
    });

    // v6.10.5: HARD CEILING. windowLoadPromise + paintWaiter both wait
    // on the 'load' event — if any single resource (slow CDN font,
    // never-resolving script, hung WebGL init) blocks it forever, the
    // Promise.all never resolves and the bar visibly stalls at 95 %
    // (the cap). Users (and screenshot tooling) saw the splash stuck
    // at "90 %" and the page never became interactive. Race the whole
    // bundle against a 10 s ceiling so the splash ALWAYS clears.
    // 4 s = MIN_DURATION (1.5 s) + ~2.5 s headroom. Long enough for
    // every real signal to resolve on a healthy load; short enough
    // that a true hang (slow CDN font, never-resolving load event)
    // clears before the user gives up. Previously had no ceiling at
    // all — splash visibly stalled at 90/95 % indefinitely.
    // When a heavy scene is pending we extend the ceiling in lockstep
    // with the scene grace above (up to ~8s), otherwise the 4s ceiling
    // would win the race and lift the splash before the galaxy paints.
    // Scene-less pages are unaffected — they keep the 4s ceiling.
    // ── Heavy remote asset(s): fully stream the route's large file(s)
    //    (the ~23 MB home Spline galaxy) into the HTTP cache so the
    //    scene component reuses it and the splash only lifts once it's
    //    100 % downloaded. No-op on light routes (empty list → resolves
    //    immediately). Drives the real sceneDlPct the bar reads above.
    const assetsPromise: Promise<unknown> = hasHeavyAsset
      ? downloadHeavyAssets(
          heavyAssets,
          (f) => {
            state.sceneDlPct = f;
            commitProgress();
          },
          assetAbort.signal,
        )
      : Promise.resolve();

    // Once the heavy file is 100% in the HTTP cache, tell the hero video it may
    // start loading. It defers its own fetch until this fires so it reads from
    // the warm cache instead of racing this download and re-buffering. The
    // video then paints + dispatches refgd:scene-ready, letting the Promise.all
    // below win the race quickly (no postDownloadGrace penalty).
    if (hasHeavyAsset) {
      void assetsPromise.then(() => {
        try {
          // Sticky flag so a hero that mounts/subscribes AFTER this fires
          // (bfcache restore, warm-cache fast resolve) still sees readiness
          // and opens its gate without waiting for the one-shot event.
          (window as unknown as Record<string, boolean>).__refgdHeavyAssetsReady =
            true;
          window.dispatchEvent(new Event("refgd:heavy-assets-ready"));
        } catch {
          /* noop */
        }
      });
    }

    const ceilingPromise = new Promise<void>((r) => {
      // Heavy-asset routes hold for a safety window so the splash can wait for
      // the full download + first paint; the real download and scene-ready
      // signals normally win this race well before the ceiling. A dead/stalled
      // network must never strand the user. (Was 60s — far too long: when the
      // scene-ready event was slow/never fired the bar visibly "stuck at 95%"
      // for up to a minute. The postDownloadGrace below now bounds the common
      // case; this is only the hard network-dead backstop.)
      if (hasHeavyAsset) {
        window.setTimeout(r, 30000);
        return;
      }
      window.setTimeout(() => {
        if (!scenePending) r();
        else window.setTimeout(r, 4000);
      }, 4000);
    });
    // Heavy routes: once the scene FILE has fully downloaded, give its first
    // paint (refgd:scene-ready) a bounded grace, then proceed even if it hasn't
    // fired. This caps the worst-case "stuck at 95%" stall at download-time +
    // grace (instead of the 30s ceiling) while still letting the scene paint
    // first on the common path (it usually parses within the grace), so the
    // overlay rarely lifts onto a blank canvas. Never resolves on light routes
    // (their Promise.all already wins the race instantly), so it can't interfere.
    const postDownloadGrace: Promise<void> = hasHeavyAsset
      ? assetsPromise.then(
          () => new Promise<void>((r) => window.setTimeout(r, 9000)),
        )
      : new Promise<void>(() => {});
    Promise.race([
      Promise.all([
        fontsReadyPromise,
        windowLoadPromise,
        minStallPromise,
        paintWaiter,
        imagesPromise,
        sceneReadyPromise,
        assetsPromise,
      ]).then(() => undefined),
      postDownloadGrace,
      ceilingPromise,
    ]).then(() => {
      if (cancelled) return;
      state.everythingResolved = true;
      // Final commit takes us from whatever real % we reached up to 100
      lastShownRef.current = 100;
      setProgress(100);
      setPhase(PHASES[4]);
      cancelAnimationFrame(rafId);

      timerA = window.setTimeout(() => {
        setVisible(false);
        // v6.10.3: gated entrance animations (CosmicJourney welcome
        // headline, GlassCard / PathCard / MeshEntrance / etc. via
        // useEntranceReady) used to fire HERE — i.e. at the moment
        // the splash STARTS its 800 ms opacity fade. The result:
        // each entrance ran its 1.0 s reveal behind a still-mostly-
        // opaque splash, finishing ~80 %% of the way through the fade.
        // By the time the splash was fully gone the welcome was
        // already nearly settled, so the user perceived it as
        // "welcome text delayed and animation not visible on page
        // load — but visible after returning to home from another
        // page" (back-nav re-mounts with the gate already open, so
        // the entrance plays cleanly there).
        //
        // Now we wait 820 ms (the full 800 ms CSS opacity transition
        // plus a 20 ms safety margin) AFTER setVisible(false) before
        // firing the gate. By then the splash is fully transparent
        // and the welcome reveal runs visibly on a clean backdrop.
        // timerC carries this so it's properly cleared on unmount.
        timerC = window.setTimeout(markLoadingComplete, 820);
      }, 280);
      timerB = window.setTimeout(() => {
        setRemoved(true);
        document.body.style.overflow = prevOverflow;
        document.body.style.touchAction = prevTouchAction;
        // v6.13.37 — record that this session has booted so any
        // later mount of LoadingScreen (hard refresh, restore from
        // bfcache) skips the splash entirely.
        markSessionLoaded();
      }, 1100);
    });

    return () => {
      cancelled = true;
      assetAbort.abort();
      cancelAnimationFrame(rafId);
      clearTimeout(discoverTimer);
      clearTimeout(timerA);
      clearTimeout(timerB);
      clearTimeout(timerC);
      window.removeEventListener("load", onWindowLoad);
      window.removeEventListener(
        "refgd:scene-pending",
        onScenePending as EventListener,
      );
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);

  // /community (and subpages) opt out of the boot splash entirely. Bail BEFORE
  // rendering the overlay so it never enters the server HTML — otherwise the
  // browser paints the full-screen cosmic overlay for a frame on a cold desktop
  // load before the mount effect can drop it (the "loading screen shows up for a
  // brief moment"). Returns null on BOTH server and first client render, so
  // hydration still matches; the mount effect above already opens the gate.
  if (splashDisabledForPath(pathname ?? "")) return null;

  if (removed) return null;

  // Cosmic twinkle backdrop (deterministic, SSR-safe positions)
  const STARS = Array.from({ length: 36 }, (_, i) => {
    const left = (i * 67 + 13) % 100;
    const top = (i * 41 + 7) % 100;
    const size = 1 + (i % 3);
    const dur = 3 + (i % 5);
    const delay = (i % 7) * 0.45;
    const tint =
      i % 5 === 0 ? "rgba(255,225,140,0.95)"
      : i % 5 === 1 ? "rgba(167,139,250,0.95)"
      : i % 5 === 2 ? "rgba(103,232,249,0.9)"
      : i % 5 === 3 ? "rgba(244,114,182,0.85)"
      : "rgba(255,255,255,0.95)";
    return { left, top, size, dur, delay, tint };
  });

  return (
    <div
      aria-hidden={!visible}
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background:
          "radial-gradient(ellipse at 30% 30%, #1b1340 0%, #0a0c1a 55%, #000 100%)",
        opacity: visible ? 1 : 0,
        transition: "opacity 800ms cubic-bezier(0.65, 0, 0.35, 1)",
        pointerEvents: visible ? "auto" : "none",
        display: "grid",
        placeItems: "center",
        willChange: "opacity",
        transform: "translateZ(0)",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 22% 28%, rgba(167,139,250,0.28), transparent 45%)," +
            "radial-gradient(circle at 78% 70%, rgba(34,211,238,0.22), transparent 50%)," +
            "radial-gradient(circle at 50% 100%, rgba(245,185,69,0.18), transparent 60%)",
          pointerEvents: "none",
        }}
      />

      <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
        {STARS.map((s, i) => (
          <span
            key={i}
            className="telegram-star"
            style={{
              position: "absolute",
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              background: s.tint,
              borderRadius: "50%",
              boxShadow: `0 0 ${s.size * 5}px ${s.tint}`,
              animationDuration: `${s.dur}s`,
              animationDelay: `${s.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Spinning 3D wireframe accent — same family of shapes that
          drifts in the page background, so the loading screen visually
          previews the experience the user is about to get. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          top: "calc(50% - 200px)",
          transform: "translate(-50%, -50%)",
          width: 110,
          height: 110,
          opacity: 0.55,
          perspective: "800px",
        }}
      >
        <div
          className="shape-gyro"
          style={{ width: "100%", height: "100%" }}
        >
          <div className="ring ring-1" />
          <div className="ring ring-2" />
          <div className="ring ring-3" />
          <div className="core" />
        </div>
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          maxWidth: 440,
          padding: "0 24px",
        }}
      >
        <div
          className="pulse-glow-violet"
          style={{
            display: "inline-grid",
            placeItems: "center",
            width: 104,
            height: 104,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 30% 30%, rgba(255,225,140,0.42), rgba(167,139,250,0.22) 55%, transparent 100%)",
            border: "1px solid rgba(255,225,140,0.35)",
            marginBottom: 28,
          }}
        >
          <div
            style={{
              fontFamily: "'Space Grotesk', Geist, system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 40,
              letterSpacing: "-0.04em",
              background:
                "linear-gradient(135deg, #ffe28a 0%, #ffffff 50%, #a78bfa 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: 1,
            }}
          >
            RG
          </div>
        </div>

        <h1
          style={{
            fontFamily: "'Space Grotesk', Geist, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "0.34em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.96)",
            margin: 0,
            marginBottom: 6,
            textShadow:
              "0 0 24px rgba(167,139,250,0.55), 0 0 48px rgba(255,225,140,0.18)",
          }}
        >
          RefundGod
        </h1>

        <p
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 11,
            letterSpacing: "0.42em",
            textTransform: "uppercase",
            color: "rgba(167,139,250,0.95)",
            margin: 0,
            marginBottom: 28,
            minHeight: "1em",
          }}
        >
          {phase}
        </p>

        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 280,
            height: 2,
            margin: "0 auto",
            background: "rgba(255,255,255,0.10)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: `${progress}%`,
              background:
                "linear-gradient(90deg, #ffe28a, #a78bfa 50%, #67e8f9)",
              borderRadius: 2,
              boxShadow: "0 0 14px rgba(167,139,250,0.85)",
              transition: "width 260ms cubic-bezier(0.4, 0, 0.2, 1)",
              willChange: "width",
            }}
          />
        </div>

        <p
          style={{
            fontFamily: "Geist, system-ui, sans-serif",
            fontSize: 10,
            color: "rgba(255,255,255,0.55)",
            margin: 0,
            marginTop: 14,
            letterSpacing: "0.22em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {progress}%
        </p>
      </div>
    </div>
  );
}
